import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NativeOverlaySheet } from "@/components/native/NativeOverlaySheet";
import { useAuth } from "@/hooks/useAuth";
import {
  getNativePushPermissionState,
  requestNativePushPermissionAndRegister,
} from "@/lib/native/pushNotifications";
import { markNativePushTipDismissed } from "@/lib/native/nativePushTipStorage";
import { settingsLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

const storageKey = (userId: string) =>
  `bilenia.nativePush.optInDismissed.${userId}`;

function wasDismissed(userId: string): boolean {
  try {
    return localStorage.getItem(storageKey(userId)) === "1";
  } catch {
    return false;
  }
}

function markDismissed(userId: string) {
  try {
    localStorage.setItem(storageKey(userId), "1");
  } catch {
    /* ignore */
  }
}

/**
 * One-time soft prompt after login on native: ask to enable OS push permission.
 * Preference toggles stay default-on; this only unlocks FCM token registration.
 * Uses the shared native bottom sheet (swipe / X / buttons).
 */
export function NativePushOptInDialog() {
  const { user, isLoading } = useAuth();
  const labels = settingsLabels(getLanguage()).notifications?.push ?? {};
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isLoading || !user?.id) {
      setOpen(false);
      return;
    }
    if (wasDismissed(user.id)) {
      setOpen(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        const state = await getNativePushPermissionState();
        if (cancelled) return;
        // Already granted / unavailable / hard-denied → no soft prompt.
        if (state === "granted" || state === "unavailable" || state === "denied") {
          if (state === "granted" || state === "denied") {
            markDismissed(user.id);
          }
          // Already enabled → hide dashboard tip as well
          if (state === "granted") {
            markNativePushTipDismissed(user.id);
          }
          setOpen(false);
          return;
        }
        setOpen(true);
      })();
    }, 1600);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user?.id, isLoading]);

  const dismiss = useCallback(() => {
    if (user?.id) markDismissed(user.id);
    setOpen(false);
  }, [user?.id]);

  const activate = useCallback(async () => {
    setBusy(true);
    try {
      const result = await requestNativePushPermissionAndRegister();
      if (result.granted && user?.id) {
        markNativePushTipDismissed(user.id);
      }
    } finally {
      setBusy(false);
      dismiss();
    }
  }, [dismiss, user?.id]);

  return (
    <NativeOverlaySheet
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss();
      }}
      title={labels.optInTitle ?? "Aktivera push-notiser?"}
      className="native-sheet-compact"
      contentClassName="!mt-2"
    >
      <div className="flex flex-col items-center text-center gap-3">
        <div className="rounded-full bg-primary/10 p-3">
          <Bell className="h-7 w-7 text-primary" aria-hidden />
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {labels.optInBody ??
            "Få notiser om bud, köp, kontrakt och leverans även när appen är stängd."}
        </p>
      </div>
      <div className="mt-5 flex flex-col gap-2">
        <Button
          type="button"
          className="w-full"
          size="lg"
          disabled={busy}
          onClick={() => void activate()}
        >
          {busy
            ? (labels.processing ?? "Behandlar...")
            : (labels.optInConfirm ?? "Ja, aktivera")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          disabled={busy}
          onClick={dismiss}
        >
          {labels.optInLater ?? "Inte nu"}
        </Button>
      </div>
    </NativeOverlaySheet>
  );
}
